import { useNavigate } from 'react-router-dom';
import { Plane } from 'lucide-react';

/**
 * Reusable ad-space placeholder. Click leads to /contact.
 * variant="horizontal" — leaderboard-style banner (full-width, short)
 * variant="sidebar"    — tall vertical card (used on airport pages)
 */
export default function AdBanner({ variant = 'horizontal', className = '' }) {
  const navigate = useNavigate();

  if (variant === 'sidebar') {
    return (
      <button
        onClick={() => navigate('/contact')}
        className={`w-full group focus:outline-none ${className}`}
        title="Advertise with Fly N Out"
        aria-label="Advertisement — click to contact us"
      >
        <div
          className="relative w-full rounded-2xl overflow-hidden border-2 border-dashed border-[#FF4500]/30 bg-gradient-to-br from-[#EBF4FF] to-[#DBEAFE] dark:from-[#1E3A5F]/30 dark:to-[#1E3A8A]/20 hover:border-[#FF4500]/60 transition-all duration-300 hover:shadow-lg hover:shadow-[#FF4500]/10"
          style={{ minHeight: '520px' }}
        >
          <div className="h-1.5 w-full bg-gradient-to-r from-[#FF4500] via-[#FF6B35] to-[#FF4500] opacity-60 group-hover:opacity-100 transition-opacity" />
          <div className="flex flex-col items-center justify-center h-full px-4 py-10 gap-6" style={{ minHeight: '520px' }}>
            <div className="w-16 h-16 rounded-full bg-[#FF4500]/10 group-hover:bg-[#FF4500]/20 transition-colors flex items-center justify-center">
              <Plane className="w-8 h-8 text-[#FF4500]" />
            </div>
            <div className="px-3 py-1 rounded-full border border-[#FF4500]/30 bg-white/60 dark:bg-black/20">
              <span className="text-[10px] font-semibold tracking-widest text-[#FF4500]/70 uppercase">Advertisement</span>
            </div>
            <div className="text-center space-y-2">
              <p className="text-sm font-bold text-foreground leading-snug" style={{ fontFamily: 'Space Grotesk' }}>
                Reach millions of<br />global travelers
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-[180px] mx-auto">
                Your brand here — connect with an engaged aviation community
              </p>
            </div>
            <div className="px-5 py-2 rounded-full bg-[#FF4500] text-white text-xs font-semibold group-hover:bg-[#E03E00] transition-colors shadow-md">
              Advertise with us
            </div>
            <p className="text-[10px] text-muted-foreground/50 mt-2">300 × 520 px</p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#FF4500] via-[#FF6B35] to-[#FF4500] opacity-40 group-hover:opacity-80 transition-opacity" />
        </div>
        <p className="text-[10px] text-muted-foreground/50 text-center mt-2 group-hover:text-[#FF4500]/60 transition-colors">
          Click to learn about advertising
        </p>
      </button>
    );
  }

  // horizontal leaderboard
  return (
    <button
      onClick={() => navigate('/contact')}
      className={`w-full group focus:outline-none ${className}`}
      title="Advertise with Fly N Out"
      aria-label="Advertisement — click to contact us"
    >
      <div className="relative w-full rounded-2xl overflow-hidden border-2 border-dashed border-[#FF4500]/25 bg-gradient-to-r from-[#EBF4FF] via-[#F0F7FF] to-[#DBEAFE] dark:from-[#1E3A5F]/20 dark:via-[#1E3A8A]/15 dark:to-[#1E3A5F]/20 hover:border-[#FF4500]/50 transition-all duration-300 hover:shadow-md hover:shadow-[#FF4500]/8">
        {/* Left accent */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FF4500] to-[#FF6B35] opacity-50 group-hover:opacity-100 transition-opacity rounded-l-2xl" />
        <div className="flex items-center justify-between gap-4 px-6 py-4 pl-8">
          {/* Left: icon + label */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 group-hover:bg-[#FF4500]/20 transition-colors flex items-center justify-center">
              <Plane className="w-5 h-5 text-[#FF4500]" />
            </div>
            <div className="hidden sm:block">
              <p className="text-[10px] font-semibold tracking-widest text-[#FF4500]/70 uppercase">Advertisement</p>
              <p className="text-xs text-muted-foreground/60">728 × 90 px</p>
            </div>
          </div>

          {/* Centre: message */}
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-bold text-foreground truncate" style={{ fontFamily: 'Space Grotesk' }}>
              Reach millions of global travelers with your brand
            </p>
            <p className="text-xs text-muted-foreground hidden md:block truncate">
              Connect with an engaged aviation community — airlines, airports, frequent flyers
            </p>
          </div>

          {/* Right: CTA */}
          <div className="px-4 py-2 rounded-full bg-[#FF4500] text-white text-xs font-semibold group-hover:bg-[#E03E00] transition-colors shadow-sm shrink-0 whitespace-nowrap">
            Advertise with us
          </div>
        </div>
      </div>
    </button>
  );
}
